/**
 * ImageKitUpload — reusable file upload widget.
 * Fetches signed auth from /api/imagekit/auth, uploads directly to ImageKit,
 * then calls onUploaded(url) with the resulting CDN URL.
 *
 * Props:
 *   folder     — ImageKit folder key: "resumes/", "logos/", "profiles/", "hero-images/"
 *   accept     — file input accept string, e.g. ".pdf,.doc,.docx" or "image/*"
 *   label      — button label
 *   onUploaded — callback(url: string)
 *   token      — JWT for /api/imagekit/auth (optional, but good practice)
 *   disabled   — boolean
 */
import React, { useRef, useState } from 'react';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function ImageKitUpload({ folder = 'uploads/', accept = '*', label = 'Upload File', onUploaded, token, disabled = false }) {
  const fileRef = useRef(null);
  const [status, setStatus]     = useState('idle'); // idle | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [errMsg, setErrMsg]     = useState('');

  async function handleChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic size guard (20 MB)
    if (file.size > 20 * 1024 * 1024) {
      setErrMsg('File too large (max 20 MB)');
      setStatus('error');
      return;
    }

    setStatus('uploading');
    setErrMsg('');
    setProgress(0);

    try {
      // 1. Get signed auth params from backend
      const authRes = await fetch(`${BASE}/api/imagekit/auth?folder=${encodeURIComponent(folder)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!authRes.ok) throw new Error('Failed to get upload credentials');
      const auth = await authRes.json();

      // 2. Upload directly to ImageKit via FormData
      const form = new FormData();
      form.append('file', file);
      form.append('fileName', file.name);
      form.append('publicKey', auth.publicKey);
      form.append('signature', auth.signature);
      form.append('expire', auth.expire);
      form.append('token', auth.token);
      form.append('folder', auth.folder || folder);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `https://upload.imagekit.io/api/v1/files/upload`);

      xhr.upload.onprogress = ev => {
        if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
      };

      await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } else {
            reject(new Error(xhr.responseText || 'Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(form);
      }).then(data => {
        setStatus('done');
        setProgress(100);
        if (onUploaded) onUploaded(data.url);
      });
    } catch (err) {
      setErrMsg(err.message || 'Upload failed');
      setStatus('error');
    } finally {
      // Reset file input so same file can be re-uploaded
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div data-testid="imagekit-upload" style={{ display:'flex', flexDirection:'column', gap:6 }}>
      <label style={{
        display:'inline-flex', alignItems:'center', gap:8, cursor: disabled ? 'not-allowed' : 'pointer',
        padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
        background: status === 'done' ? '#f0fdf4' : 'rgba(27,107,114,.1)',
        border: `1.5px solid ${status === 'done' ? '#86efac' : '#1B6B72'}`,
        color: status === 'done' ? '#166534' : '#1B6B72',
        opacity: disabled ? 0.5 : 1,
        transition: 'all .15s',
        alignSelf:'flex-start',
      }}>
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          disabled={disabled || status === 'uploading'}
          style={{ display:'none' }}
          data-testid="imagekit-file-input"
        />
        {status === 'uploading' ? (
          <>
            <span style={{ width:14, height:14, border:'2px solid #1B6B72', borderTopColor:'transparent', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }} />
            Uploading {progress}%…
          </>
        ) : status === 'done' ? (
          <> ✓ Uploaded successfully</>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
            </svg>
            {label}
          </>
        )}
      </label>

      {status === 'uploading' && (
        <div style={{ width:'100%', height:4, background:'#e5e0d6', borderRadius:2, overflow:'hidden' }}>
          <div style={{ width:`${progress}%`, height:'100%', background:'#1B6B72', transition:'width .2s' }} />
        </div>
      )}

      {status === 'error' && (
        <div style={{ fontSize:12, color:'#991b1b', fontFamily:'Inter,sans-serif' }} data-testid="upload-error">
          {errMsg}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
